import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Skeleton,
  SkeletonItem,
} from "@fluentui/react-components";
import {
  AddFilled,
  ArrowUpRightRegular,
  Checkmark12Regular,
  ChevronDown12Regular,
  DataPieRegular,
  EyeRegular,
  GlobeRegular,
  LinkRegular,
  SearchRegular,
} from "@fluentui/react-icons";
import {
  deleteProject,
  exportProject,
  getProjectStatus,
} from "apps/decooda/decoodaapi";
import { IProject } from "apps/decooda/components/projects/projectCard.types";
import { ProjectStatus } from "apps/decooda/components/projects/projectConstants";
import { ScoreProjectDialog } from "apps/decooda/components/projects/ScoreProjectDialog";
import { getWorkspaceAnalytics } from "apps/project-iq/api";
import { useLinks } from "apps/project-iq/hooks/useLinks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { CreateAnalyticsDialog } from "../CreateAnalyticsDialog/CreateAnalyticsDialog";
import { AnalyticsCard } from "./AnalyticsCard";
import { useStyles } from "./WorkspaceAnalytics.styles";
import {
  AnalyticsSortBy,
  IWorkspaceAnalyticsProps,
} from "./WorkspaceAnalytics.types";

const SORT_OPTIONS: { value: AnalyticsSortBy; label: string }[] = [
  { value: "last_updated", label: "Last updated" },
  { value: "name", label: "Name" },
  { value: "date_created", label: "Date created" },
];

const SKELETON_CARD_COUNT = 4;
const QUICK_LINK_SKELETON_COUNT = 3;
const POLL_INTERVAL_MS = 5000;

const AnalyticsCardSkeleton = (): JSX.Element => {
  const styles = useStyles();
  return (
    <div className={styles.skeletonCard}>
      <Skeleton>
        <div className={styles.skeletonCardInner}>
          <SkeletonItem animation="wave" className={styles.skeletonCardIcon} />
          <div className={styles.skeletonCardBody}>
            <div className={styles.skeletonCardNameRow}>
              <SkeletonItem
                animation="wave"
                className={styles.skeletonCardName}
              />
              <SkeletonItem
                animation="wave"
                className={styles.skeletonCardPill}
              />
            </div>
            <SkeletonItem
              animation="wave"
              className={styles.skeletonCardDescription}
            />
            <SkeletonItem
              animation="wave"
              className={styles.skeletonCardMeta}
            />
          </div>
          <SkeletonItem animation="wave" className={styles.skeletonCardArrow} />
        </div>
      </Skeleton>
    </div>
  );
};

const QuickLinkCardSkeleton = (): JSX.Element => {
  const styles = useStyles();
  return (
    <Skeleton>
      <div className={styles.quickLinkCard} style={{ pointerEvents: "none" }}>
        <SkeletonItem
          animation="wave"
          className={styles.quickLinkSkeletonIcon}
        />
        <div className={styles.quickLinkContent}>
          <SkeletonItem
            animation="wave"
            className={styles.quickLinkSkeletonTitle}
          />
          <SkeletonItem
            animation="wave"
            className={styles.quickLinkSkeletonSubtitle}
          />
        </div>
      </div>
    </Skeleton>
  );
};

export const WorkspaceAnalytics = ({
  workspaceId,
  workspace,
}: IWorkspaceAnalyticsProps): JSX.Element => {
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortBy, setSortBy] = useState<AnalyticsSortBy>("last_updated");
  const [isSortMenuOpen, setIsSortMenuOpen] = useState<boolean>(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState<boolean>(false);
  const [editingProject, setEditingProject] = useState<IProject | null>(null);
  const [deletingProject, setDeletingProject] = useState<IProject | null>(null);
  const [isDeletingProject, setIsDeletingProject] = useState<boolean>(false);
  const [scoringProject, setScoringProject] = useState<IProject | null>(null);
  const [exportingProjectId, setExportingProjectId] = useState<string | null>(
    null,
  );
  const [exportError, setExportError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const {
    data: projects,
    isLoading,
    isFetching,
  } = useQuery<IProject[], Error>(
    ["workspace-analytics", workspaceId],
    () => getWorkspaceAnalytics({ workspaceId }),
    {
      enabled: !!workspaceId,
      refetchOnWindowFocus: false,
    },
  );

  const { links: quickLinks, isLoading: isQuickLinksLoading } = useLinks(
    "ai_analytics_quick_links",
  );

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const projectsRef = useRef<IProject[]>(projects ?? []);

  useEffect(() => {
    projectsRef.current = projects ?? [];
  }, [projects]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollScoringProjects = useCallback(async () => {
    const scoringProjects = projectsRef.current.filter(
      (p) => p.status === ProjectStatus.InProgress,
    );

    if (scoringProjects.length === 0) {
      stopPolling();
      return;
    }

    for (const project of scoringProjects) {
      try {
        const response = await getProjectStatus({ projectId: project.id });
        if (response.status !== ProjectStatus.InProgress) {
          await queryClient.invalidateQueries([
            "workspace-analytics",
            workspaceId,
          ]);
          break;
        }
      } catch {
        void 0;
      }
    }
  }, [stopPolling, queryClient, workspaceId]);

  useEffect(() => {
    const hasScoringProjects = (projects ?? []).some(
      (p) => p.status === ProjectStatus.InProgress,
    );

    if (hasScoringProjects && !pollingRef.current) {
      pollingRef.current = setInterval(pollScoringProjects, POLL_INTERVAL_MS);
    } else if (!hasScoringProjects && pollingRef.current) {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [projects, pollScoringProjects, stopPolling]);

  const filteredProjects = useMemo(() => {
    const list: IProject[] = projects ?? [];

    const searched = searchQuery.trim()
      ? list.filter((p) => {
          const query = searchQuery.toLowerCase();
          return (
            p.name?.toLowerCase().includes(query) ||
            p.objective?.toLowerCase().includes(query)
          );
        })
      : list;

    return [...searched].sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "date_created") {
        return (
          new Date(b.created ?? b.createdAt ?? 0).getTime() -
          new Date(a.created ?? a.createdAt ?? 0).getTime()
        );
      }
      return (
        new Date(b.modifiedAt ?? b.created ?? 0).getTime() -
        new Date(a.modifiedAt ?? a.created ?? 0).getTime()
      );
    });
  }, [projects, searchQuery, sortBy]);

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deletingProject) return;
    setIsDeletingProject(true);
    setDeleteError(null);
    try {
      await deleteProject({ projectId: deletingProject.id });
      await queryClient.invalidateQueries(["workspace-analytics", workspaceId]);
      setDeletingProject(null);
    } catch {
      setDeleteError(
        `Failed to delete "${deletingProject.name}". Please try again.`,
      );
    } finally {
      setIsDeletingProject(false);
    }
  };

  const handleExport = async (project: IProject): Promise<void> => {
    setExportingProjectId(project.id);
    setExportError(null);
    try {
      await exportProject({ projectId: project.id });
    } catch {
      setExportError(
        `Export failed for "${project.name}". The project may not have scored results yet — try re-scoring.`,
      );
    } finally {
      setExportingProjectId(null);
    }
  };

  const handleScoringStarted = useCallback((): void => {
    queryClient.invalidateQueries(["workspace-analytics", workspaceId]);
  }, [queryClient, workspaceId]);

  const handleScoringComplete = useCallback((): void => {
    queryClient.invalidateQueries(["workspace-analytics", workspaceId]);
  }, [queryClient, workspaceId]);

  const isLoadingProjects = isLoading || isFetching;
  const currentSortLabel =
    SORT_OPTIONS.find((option) => option.value === sortBy)?.label ??
    "Last updated";

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div className={styles.content}>
          <div className={styles.hero}>
            <div className={styles.heroEyebrow} aria-hidden="true">
              <DataPieRegular className={styles.heroEyebrowIcon} />
              <span>AI Analytics</span>
            </div>
            <h2 className={styles.heroTitle}>Analyze engagement data</h2>
            <p className={styles.heroDescription}>
              Browse the analytics projects already running on this engagement,
              or start a new one. AI Analytics discovers topics, sentiment and
              emotions across your survey exports, interview transcripts and
              open-text feedback.
            </p>
          </div>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionBadge} aria-hidden="true">
                <GlobeRegular className={styles.sectionBadgeIcon} />
              </div>
              <h3 className={styles.sectionTitle}>Quick links</h3>
            </div>
            <div className={styles.quickLinksGrid}>
              {isQuickLinksLoading
                ? Array.from({ length: QUICK_LINK_SKELETON_COUNT }).map(
                    (_, i) => <QuickLinkCardSkeleton key={i} />,
                  )
                : quickLinks.map((link) => (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${link.displayName} (opens in new tab)`}
                      className={styles.quickLinkCard}
                    >
                      <div
                        className={styles.quickLinkIconContainer}
                        aria-hidden="true"
                      >
                        <LinkRegular />
                      </div>
                      <div className={styles.quickLinkContent}>
                        <div className={styles.quickLinkTitle}>
                          {link.displayName}
                        </div>
                        {link.subtext && (
                          <div className={styles.quickLinkSubtitle}>
                            {link.subtext}
                          </div>
                        )}
                      </div>
                      <div className={styles.quickLinkArrow} aria-hidden="true">
                        <ArrowUpRightRegular />
                      </div>
                    </a>
                  ))}
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionBadge} aria-hidden="true">
                <EyeRegular className={styles.sectionBadgeIcon} />
              </div>
              <div className={styles.sectionTitleGroup}>
                <h3 className={styles.sectionTitle}>Analytics Projects</h3>
                <p className={styles.sectionDescription}>
                  Every analytics project run on this engagement.
                </p>
              </div>
              <div className={styles.sectionAction}>
                <Button
                  appearance="primary"
                  icon={<AddFilled />}
                  className={styles.newAnalyticsButton}
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  New Analytics Project
                </Button>
              </div>
            </div>

            <div className={styles.analyticsControls}>
              <div className={styles.searchWrapper}>
                <span className={styles.searchIcon} aria-hidden="true">
                  <SearchRegular />
                </span>
                <input
                  type="search"
                  className={styles.searchNativeInput}
                  placeholder="Search analytics projects by name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search analytics projects"
                />
              </div>
              <div className={styles.sortWrapper}>
                <Popover
                  open={isSortMenuOpen}
                  onOpenChange={(_, data) => setIsSortMenuOpen(data.open)}
                  positioning="below-end"
                >
                  <PopoverTrigger disableButtonEnhancement>
                    <button
                      className={styles.sortTrigger}
                      aria-label={`Sort by: ${currentSortLabel}`}
                      aria-haspopup="listbox"
                      aria-expanded={isSortMenuOpen}
                    >
                      <span
                        className={styles.sortTriggerText}
                        aria-hidden="true"
                      >
                        Sort by · {currentSortLabel}
                      </span>
                      <ChevronDown12Regular
                        className={mergeClasses(
                          styles.sortTriggerChevron,
                          isSortMenuOpen && styles.sortTriggerChevronOpen,
                        )}
                        aria-hidden="true"
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverSurface
                    className={styles.sortPopoverSurface}
                    role="listbox"
                  >
                    {SORT_OPTIONS.map((option) => {
                      const isActive = option.value === sortBy;
                      return (
                        <button
                          key={option.value}
                          className={mergeClasses(
                            styles.sortMenuItem,
                            isActive && styles.sortMenuItemActive,
                          )}
                          onClick={() => {
                            setSortBy(option.value);
                            setIsSortMenuOpen(false);
                          }}
                          role="option"
                          aria-selected={isActive}
                        >
                          <span className={styles.sortMenuItemLabel}>
                            {option.label}
                          </span>
                          {isActive && (
                            <Checkmark12Regular aria-hidden="true" />
                          )}
                        </button>
                      );
                    })}
                  </PopoverSurface>
                </Popover>
              </div>
            </div>

            {exportError && (
              <div role="alert" className={styles.exportErrorBanner}>
                {exportError}
                <button
                  className={styles.exportErrorDismiss}
                  aria-label="Dismiss export error"
                  onClick={() => setExportError(null)}
                >
                  ×
                </button>
              </div>
            )}

            <div>
              {isLoadingProjects ? (
                <div className={styles.analyticsList}>
                  {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
                    <AnalyticsCardSkeleton key={i} />
                  ))}
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className={styles.emptyState}>
                  {searchQuery.trim()
                    ? "No analytics projects match your search."
                    : "No analytics projects yet. Use the button above to create your first."}
                </div>
              ) : (
                <div className={styles.analyticsList}>
                  {filteredProjects.map((project) => (
                    <AnalyticsCard
                      key={project.id}
                      project={project}
                      onNavigate={() =>
                        navigate(
                          `/workspace/${workspaceId}/ai-analytics/${project.id}`,
                          { state: { project } },
                        )
                      }
                      onEdit={() => setEditingProject(project)}
                      onDelete={() => setDeletingProject(project)}
                      onScore={() => setScoringProject(project)}
                      onExport={() => void handleExport(project)}
                      isExporting={exportingProjectId === project.id}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <CreateAnalyticsDialog
        isOpen={isCreateDialogOpen}
        workspaceId={workspaceId}
        workspace={workspace}
        onDismiss={() => setIsCreateDialogOpen(false)}
      />

      {editingProject && (
        <CreateAnalyticsDialog
          isOpen={true}
          mode="edit"
          workspaceId={workspaceId}
          workspace={workspace}
          project={editingProject}
          onSaved={() => {
            queryClient.invalidateQueries(["workspace-analytics", workspaceId]);
            setEditingProject(null);
          }}
          onDismiss={() => setEditingProject(null)}
        />
      )}

      {scoringProject && (
        <ScoreProjectDialog
          isOpen={true}
          project={scoringProject}
          onDismiss={() => setScoringProject(null)}
          onScoringStarted={handleScoringStarted}
          onScoringComplete={handleScoringComplete}
        />
      )}

      <Dialog
        open={deletingProject !== null}
        modalType="alert"
        onOpenChange={(_, data) => {
          if (!data.open) {
            setDeletingProject(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              Delete &ldquo;{deletingProject?.name}&rdquo;?
            </DialogTitle>
            <DialogContent>
              Delete this analytics project? This action cannot be undone.
              {deleteError && (
                <div role="alert" style={{ color: "red", marginTop: "8px" }}>
                  {deleteError}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeletingProject(null)}
                disabled={isDeletingProject}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleConfirmDelete}
                disabled={isDeletingProject}
              >
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
